/**
 * Expiring Contracts Table Row Component
 * US-CONT-005: View Expiring Contracts Alert
 * Individual table row for contract display
 */

import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ContractAction, ExpiringContractWithUrgency } from '../types/expiringContracts.types';
import { ContractUrgencyBadge } from './ContractUrgencyBadge';
import { ContractQuickActions } from './ContractQuickActions';
import { formatExpiryDate, formatContractValue, getContractTypeLabel } from '../utils/contractFormatters.utils';

interface ExpiringContractsTableRowProps {
  contract: ExpiringContractWithUrgency;
  onAction: (action: ContractAction) => void;
  isRenewing?: boolean;
  onRowClick?: (contract: ExpiringContractWithUrgency) => void;
  className?: string;
}

export function ExpiringContractsTableRow({
  contract,
  onAction,
  isRenewing = false,
  onRowClick,
  className
}: ExpiringContractsTableRowProps) {
  const { urgency } = contract;

  function handleRowClick() {
    if (onRowClick) {
      onRowClick(contract);
    }
  }

  return (
    <TableRow
      className={cn(
        'hover:bg-muted/50 transition-colors',
        onRowClick && 'cursor-pointer',
        className
      )}
      onClick={handleRowClick}
    >
      {/* Customer Name */}
      <TableCell className="font-medium">
        <div className="flex flex-col">
          <span className="text-sm">{contract.customerName}</span>
          {contract.customerEmail && (
            <span className="text-xs text-muted-foreground">{contract.customerEmail}</span>
          )}
        </div>
      </TableCell>

      {/* Contract Number */}
      <TableCell>
        <code className="text-xs bg-muted px-2 py-1 rounded">
          {contract.contractNumber}
        </code>
      </TableCell>

      {/* Contract Type */}
      <TableCell className="hidden md:table-cell">
        {getContractTypeLabel(contract.contractType)}
      </TableCell>

      {/* Contract Value */}
      <TableCell className="hidden lg:table-cell text-right">
        {formatContractValue(contract.contractValue)}
      </TableCell>

      {/* Expiry Date */}
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm">{formatExpiryDate(contract.endDate)}</span>
          <span className="text-xs text-muted-foreground">
            {urgency.daysRemaining >= 0 ? 'Expires in' : 'Expired'} {Math.abs(urgency.daysRemaining)} days
          </span>
        </div>
      </TableCell>

      {/* Urgency Badge */}
      <TableCell>
        <ContractUrgencyBadge
          urgency={urgency.urgencyLevel}
          daysRemaining={urgency.daysRemaining}
          showLabel={false}
        />
      </TableCell>

      {/* Actions */}
      <TableCell className="text-right">
        <ContractQuickActions
          contract={contract}
          onAction={onAction}
          isRenewing={isRenewing}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * Skeleton loader for table row
 */
export function ExpiringContractsTableRowSkeleton() {
  return (
    <TableRow>
      <TableCell>
        <div className="space-y-2">
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
          <div className="h-3 w-40 bg-muted animate-pulse rounded" />
        </div>
      </TableCell>
      <TableCell>
        <div className="h-6 w-24 bg-muted animate-pulse rounded" />
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="h-4 w-28 bg-muted animate-pulse rounded" />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <div className="h-4 w-20 bg-muted animate-pulse rounded" />
      </TableCell>
      <TableCell>
        <div className="space-y-2">
          <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          <div className="h-3 w-28 bg-muted animate-pulse rounded" />
        </div>
      </TableCell>
      <TableCell>
        <div className="h-6 w-16 bg-muted animate-pulse rounded" />
      </TableCell>
      <TableCell>
        <div className="h-8 w-8 bg-muted animate-pulse rounded ml-auto" />
      </TableCell>
    </TableRow>
  );
}
